import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { subscriptionService } from '../../services/subscription/subscriptionService';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const hasToken = typeof window !== 'undefined' && Boolean(localStorage.getItem('token_agents'));
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [isTrialPendingVoucher, setIsTrialPendingVoucher] = useState(false);
  const [subscriptionDaysRemaining, setSubscriptionDaysRemaining] = useState(0);
  const [loading, setLoading] = useState(hasToken);
  const [initialLoaded, setInitialLoaded] = useState(!hasToken);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [vouchersCreated, setVouchersCreated] = useState(0);
  const [vouchersLimit, setVouchersLimit] = useState(10);
  const [cycleStartDate, setCycleStartDate] = useState(null);
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [latestRequest, setLatestRequest] = useState(null);

  const refreshSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token_agents');
      if (!token) {
        setSubscription(null);
        setInitialLoaded(true);
        setLoading(false);
        return;
      }

      const data = await subscriptionService.getMySubscription();
      const subData = data.subscription || null;
      setSubscription(subData);

      setCurrentPlan(data.subscription?.plan_id || null);
      setLatestRequest(data.latestRequest || null);
      setTrialDaysRemaining(data.trialDaysRemaining || 0);
      setIsTrialExpired(data.isTrialExpired || false);
      setIsTrialPendingVoucher(data.isTrialPendingVoucher || Boolean(data.subscription?.status === 'trial' && !data.subscription?.trial_start));
      setSubscriptionDaysRemaining(data.subscriptionDaysRemaining || 0);
      setVouchersCreated(data.vouchersCreated || 0);
      setVouchersLimit(data.vouchersLimit || 50);
      setCycleStartDate(data.cycleStartDate || null);
      setSupplierInfo(data.supplierInfo || null);

      try {
        const historyData = await subscriptionService.getPaymentHistory();
        setPaymentHistory(historyData.payments || []);
      } catch (historyErr) {
        console.warn('Failed to load payment history:', historyErr.message);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error.message);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  // Realtime polling when a mandate offer is pending
  useEffect(() => {
    let interval = null;
    if (subscription?.pending_change?.status === 'mandate_pending') {
      interval = setInterval(() => {
        refreshSubscription();
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [subscription?.pending_change?.status, refreshSubscription]);

  const cancelSubscription = async (reason) => {
    try {
      setLoading(true);
      await subscriptionService.cancelSubscription(reason);
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to cancel subscription:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const pauseSubscription = async (months) => {
    try {
      setLoading(true);
      await subscriptionService.pauseSubscription(months);
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to pause subscription:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resumeSubscription = async () => {
    try {
      setLoading(true);
      await subscriptionService.resumeSubscription();
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to resume subscription:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription on mount
  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  // Re-fetch subscription when the auth token changes (login/logout events)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token_agents') {
        if (e.newValue) {
          // Token was set (login) — refresh subscription data
          refreshSubscription();
        } else {
          // Token was removed (logout) — clear subscription state
          setSubscription(null);
          setCurrentPlan(null);
          setTrialDaysRemaining(0);
          setIsTrialExpired(false);
          setIsTrialPendingVoucher(false);
          setSubscriptionDaysRemaining(0);
          setPaymentHistory([]);
          setVouchersCreated(0);
          setVouchersLimit(50);
          setCycleStartDate(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshSubscription]);

  // Auto-recover pending checkout if user closed Razorpay popup early after payment
  const checkPendingCheckoutAutoRecovery = useCallback(async () => {
    try {
      const rawPending = localStorage.getItem('trrip_pending_checkout') || sessionStorage.getItem('trrip_pending_checkout');
      if (!rawPending) return;

      const pending = JSON.parse(rawPending);
      if (!pending.subscription_id) {
        localStorage.removeItem('trrip_pending_checkout');
        sessionStorage.removeItem('trrip_pending_checkout');
        return;
      }

      // Check age — if older than 2 hours, discard
      if (pending.timestamp && Date.now() - pending.timestamp > 2 * 60 * 60 * 1000) {
        localStorage.removeItem('trrip_pending_checkout');
        sessionStorage.removeItem('trrip_pending_checkout');
        return;
      }

      const res = await subscriptionService.checkPendingCheckout({
        subscription_id: pending.subscription_id,
        plan_id: pending.plan_id,
        billing_interval: pending.billing_interval
      });

      if (res.verified) {
        localStorage.removeItem('trrip_pending_checkout');
        sessionStorage.removeItem('trrip_pending_checkout');
        toast.success('Plan updated successfully! Your subscription is now active.');
        refreshSubscription();
        if (window.location.pathname.includes('/checkout')) {
          window.location.href = '/subscription';
        }
      }
    } catch (err) {
      console.warn('Pending checkout check error:', err.message);
    }
  }, [refreshSubscription]);

  // Run pending checkout auto-recovery on mount and window focus
  useEffect(() => {
    checkPendingCheckoutAutoRecovery();

    const handleFocus = () => {
      checkPendingCheckoutAutoRecovery();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkPendingCheckoutAutoRecovery]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        currentPlan,
        latestRequest,
        trialDaysRemaining,
        isTrialExpired,
        isTrialPendingVoucher,
        subscriptionDaysRemaining,
        loading,
        initialLoaded,
        paymentHistory,
        vouchersCreated,
        vouchersLimit,
        cycleStartDate,
        supplierInfo,
        refreshSubscription,
        cancelSubscription,
        pauseSubscription,
        resumeSubscription
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
